/**
 * Account deletion request: `POST ${userApiUrl}/profile/deletion-request`, no body. The Owner Hub
 * backend emails the owner (and a copy to the data protection team); nothing is deleted by the app.
 *
 * `userApiUrl` already ends in `/user`, so the path is appended directly (never `/user/profile/...`,
 * which would double the segment). The raw access token goes in `Authorization` (no `Bearer`),
 * matching the other Owner Hub calls. The token is an ARGUMENT, not read from `@/store` here: importing
 * the store runs the root saga, which hangs a Playwright CT mount.
 *
 * `_owsNoAuthTeardown`: the global auth interceptor still refreshes and replays on a 401, but when
 * that fails (or on the 403 this route answers) it rejects to the caller instead of logging the owner
 * out, so a refused request shows the card's error line rather than ejecting them.
 */
import axios from 'axios';

/** The endpoint, built from the already-registered `userApiUrl`. Read per call so dev config injection timing never matters. */
export function deletionRequestUrl(): string {
  const configured = (window as unknown as { config?: { userApiUrl?: string } }).config?.userApiUrl;
  if (configured === undefined || configured === null || configured === '') {
    throw new Error('window.config.userApiUrl is not configured');
  }
  return `${configured.replace(/\/+$/, '')}/profile/deletion-request`;
}

/** True only for the contract's success body, `{ ok: true }`. */
const isOk = (body: unknown): boolean =>
  body !== null && typeof body === 'object' && 'ok' in body && (body as { ok: unknown }).ok === true;

/**
 * Asks the backend to start an account deletion request for the signed-in owner. Resolves once the
 * backend answers `200 { ok: true }`; rejects on a missing token, a non-2xx answer
 * (`{ error: { code, message } }`), or a 200 whose body is anything else (e.g. an HTML fallback page
 * served for a route that is not deployed yet), so the caller never reports a request it did not make.
 */
export async function requestAccountDeletion(token: string | null): Promise<void> {
  if (token === null || token === '') {
    throw new Error('No authentication token found');
  }
  const response = await axios.post<unknown>(deletionRequestUrl(), undefined, {
    headers: { Authorization: token },
    _owsNoAuthTeardown: true,
  });
  if (!isOk(response.data)) {
    throw new Error('Unexpected answer from the deletion request endpoint');
  }
}
